import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DialPad from './dialer/DialPad';
import SEO from './layout/SEO';
import './HomePage.css';

const HomePage = ({ onCall, walletBalance }) => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const homeSchema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Organization",
                "name": "Ringez",
                "url": "https://www.ringez.com",
                "logo": "https://www.ringez.com/static/media/ringez.png",
                "description": "Private, instant international calling service. No signup required.",
                "sameAs": []
            },
            {
                "@type": "WebSite",
                "url": "https://www.ringez.com",
                "name": "Ringez",
                "description": "Make international calls privately from your browser",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": "https://www.ringez.com/search?q={search_term_string}",
                    "query-input": "required name=search_term_string"
                }
            },
            {
                "@type": "SoftwareApplication",
                "name": "Ringez Calling Tool",
                "applicationCategory": "CommunicationApplication",
                "offers": {
                    "@type": "Offer",
                    "price": "0.15",
                    "priceCurrency": "USD",
                    "priceSpecification": {
                        "@type": "UnitPriceSpecification",
                        "price": "0.15",
                        "priceCurrency": "USD",
                        "unitText": "per minute"
                    }
                },
                "operatingSystem": "Web Browser",
                "description": "Browser-based international calling tool"
            }
        ]
    };

    const popularCountries = [
        { name: 'USA', code: '+1', flag: '🇺🇸' },
        { name: 'India', code: '+91', flag: '🇮🇳' },
        { name: 'UK', code: '+44', flag: '🇬🇧' },
        { name: 'Canada', code: '+1', flag: '🇨🇦' },
        { name: 'Philippines', code: '+63', flag: '🇵🇭' },
        { name: 'Mexico', code: '+52', flag: '🇲🇽' },
        { name: 'Germany', code: '+49', flag: '🇩🇪' },
        { name: 'Australia', code: '+61', flag: '🇦🇺' },
        { name: 'France', code: '+33', flag: '🇫🇷' },
        { name: 'China', code: '+86', flag: '🇨🇳' },
        { name: 'Pakistan', code: '+92', flag: '🇵🇰' },
        { name: 'Brazil', code: '+55', flag: '🇧🇷' }
    ];

    const faqs = [
        {
            question: 'Do I need to create an account?',
            answer: "No! That's the beauty of Ringez. Just load credits and start calling. No email, no phone number, no verification required."
        },
        {
            question: 'How much does it cost?',
            answer: 'Calls are charged at $0.15/minute to most countries, billed per minute (rounded up). You only pay for minutes used - no monthly fees or subscriptions.'
        },
        {
            question: 'When does billing start?',
            answer: 'Billing starts as soon as the call connects to the network - even if the recipient doesn\'t answer, is busy, or the call goes to voicemail. You are charged for the full call duration from network connection.'
        },
        {
            question: 'What countries can I call?',
            answer: 'Over 200 countries and territories worldwide, including USA, India, UK, Philippines, Mexico, China, Pakistan, Canada, and more.'
        },
        {
            question: 'Do I need to download an app?',
            answer: 'No! Ringez works directly in your web browser. It\'s compatible with Chrome, Firefox, Safari, and Edge on Windows, Mac, or Linux.'
        },
        {
            question: 'Is it secure and private?',
            answer: 'Yes. Ringez doesn\'t require personal information to use. We offer a Private mode for enhanced anonymity. All calls are encrypted using modern VoIP technology.'
        },
        {
            question: 'Can I call mobile and landline numbers?',
            answer: 'Yes! Ringez works with both mobile and landline numbers in supported countries.'
        },
        {
            question: 'What if I have issues or need support?',
            answer: 'Visit our Help Center or email us at support@ringez.com. We typically respond within 24 hours.'
        },
        {
            question: 'Are emergency numbers blocked?',
            answer: 'Yes. For security, compliance, and fraud prevention, emergency numbers (911, 112, 999, etc.) and premium/high-cost prefixes are blocked.'
        }
    ];

    return (
        <div className="homepage" id="top">
            <SEO
                title="Private International Calling - No Signup Required"
                description="Make private international calls instantly from your browser. No account, no phone number required. Secure, anonymous calling to 200+ countries. Start now!"
                canonical="/"
                schema={homeSchema}
            />
            {/* Hero Section */}
            <section className="hero-section">
                <h1 className="hero-title">
                    Private International Calling<br />
                    <span className="hero-title-line2">No Signup Required</span>
                </h1>
                <p className="hero-subtitle">
                    Make international calls in seconds from your browser. No phone number required, no app to download.
                    Just load credits and dial. Your privacy protected.
                </p>

                {/* Embedded Dialer */}
                <div className="dialer-wrapper">
                    <DialPad onCall={onCall} walletBalance={walletBalance} />
                </div>

                {/* Secondary CTA */}
                <a href="#how-it-works" className="how-it-works-link">
                    How It Works ↓
                </a>
            </section>

            {/* Section 2: How It Works */}
            {/* Section 2: How It Works */}
            <section className="content-section" id="how-it-works">
                <div className="section-container split-layout flip-mobile">
                    <div className="section-image-wrapper">
                        <img src="/assets/images/device_mockup.png" alt="Ringez Device Mockup" className="section-image animate-float" />
                    </div>
                    <div className="section-content">
                        <h2 className="section-title text-left">How It Works</h2>
                        <div className="steps-vertical">
                            <div className="step-item">
                                <div className="step-number">1</div>
                                <div>
                                    <h3>Load Credits</h3>
                                    <p>Add funds securely using PayPal or UPI. Pay only for what you use.</p>
                                </div>
                            </div>
                            <div className="step-item">
                                <div className="step-number">2</div>
                                <div>
                                    <h3>Enter Phone Number</h3>
                                    <p>Type the international number with country code directly in the browser.</p>
                                </div>
                            </div>
                            <div className="step-item">
                                <div className="step-number">3</div>
                                <div>
                                    <h3>Press Call</h3>
                                    <p>Click call and start talking. Crystal clear quality, no app needed.</p>
                                </div>
                            </div>
                        </div>
                        <p className="section-note text-left">No signup, no app download. Just instant calling.</p>
                    </div>
                </div>
            </section>

            {/* Section 3: Why Choose Ringez */}
            {/* Section 3: Why Choose Ringez */}
            <section className="content-section alt-bg">
                <div className="section-container split-layout">
                    <div className="section-content">
                        <h2 className="section-title text-left">Why Choose Ringez?</h2>
                        <div className="benefits-list">
                            <div className="benefit-item">
                                <div className="benefit-icon">🚫</div>
                                <div>
                                    <h3>No Signup Required</h3>
                                    <p>No account, no email, no phone verification. Just privacy.</p>
                                </div>
                            </div>
                            <div className="benefit-item">
                                <div className="benefit-icon">🔒</div>
                                <div>
                                    <h3>Complete Privacy</h3>
                                    <p>Calls aren't linked to your identity. 100% anonymous option.</p>
                                </div>
                            </div>
                            <div className="benefit-item">
                                <div className="benefit-icon">🌐</div>
                                <div>
                                    <h3>Browser-Based Calling</h3>
                                    <p>Works on any device. No software installation needed.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="section-image-wrapper">
                        <img src="/assets/images/people_connecting.png" alt="People Connecting Globally" className="section-image glow-effect" />
                    </div>
                </div>
            </section>

            {/* Section 4: Supported Countries */}
            <section className="content-section">
                <h2 className="section-title">Call 200+ Countries Worldwide</h2>
                <p className="section-intro">
                    Ringez supports international calling to over 200 countries and territories. Whether you're calling family in India,
                    a business contact in Germany, or a friend in Brazil, we've got you covered.
                </p>
                <div className="countries-grid">
                    {popularCountries.map((country, index) => (
                        <div key={index} className="country-card">
                            <span className="country-flag">{country.flag}</span>
                            <span className="country-name">{country.name}</span>
                            <span className="country-code">{country.code}</span>
                        </div>
                    ))}
                </div>
                <p className="section-note">...and 190+ more countries supported</p>
            </section>

            {/* Section 5: FAQ */}
            <section className="content-section alt-bg" id="faq">
                <h2 className="section-title">Frequently Asked Questions</h2>
                <div className="faq-list">
                    {faqs.map((faq, index) => (
                        <div
                            key={index}
                            className={`faq-item ${openFaq === index ? 'open' : ''}`}
                            onClick={() => toggleFaq(index)}
                        >
                            <div className="faq-question">
                                <span>{faq.question}</span>
                                <span className="faq-toggle">{openFaq === index ? '−' : '+'}</span>
                            </div>
                            {openFaq === index && (
                                <div className="faq-answer">{faq.answer}</div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* Section 6: Getting Started */}
            <section className="content-section">
                <h2 className="section-title">Get Started in Under 60 Seconds</h2>
                <div className="getting-started">
                    <h3>First Time Using Ringez?</h3>
                    <ol className="start-steps">
                        <li><strong>Load Credits:</strong> Click the "Refill to Call" button above and select an amount. We accept PayPal (USD) and UPI (INR). Your credits are available immediately.</li>
                        <li><strong>Allow Microphone Access:</strong> Your browser will ask permission to use your microphone. Click "Allow" - this lets you speak during calls.</li>
                        <li><strong>Enter the Number:</strong> Type the complete phone number with country code. For example: +91 for India, +1 for USA, +44 for UK.</li>
                        <li><strong>Make Your Call:</strong> Click the call button. The call will connect within seconds. Speak clearly - you're making a real phone call!</li>
                    </ol>

                    <div className="tips-box">
                        <h4>Tips for Best Quality:</h4>
                        <ul>
                            <li>Use a quiet environment</li>
                            <li>Stable WiFi or ethernet connection recommended</li>
                            <li>Use headphones for better audio quality</li>
                            <li>Close bandwidth-heavy apps while calling</li>
                        </ul>
                    </div>

                    <a href="#top" className="scroll-to-dialer">Try It Now - Scroll Up to Dialer ↑</a>
                </div>
            </section>

            {/* Section 7: Privacy & Security */}
            <section className="content-section alt-bg">
                <h2 className="section-title">Your Privacy, Protected</h2>
                <p className="section-intro">
                    At Ringez, we believe communication should be private. That's why we've built a calling service that doesn't require your personal information.
                </p>
                <div className="privacy-grid">
                    <div className="privacy-column">
                        <h3>Privacy Features</h3>
                        <ul className="privacy-list">
                            <li>✓ No account or registration required</li>
                            <li>✓ No phone number verification</li>
                            <li>✓ No email address needed</li>
                            <li>✓ Private mode available for anonymous calling</li>
                            <li>✓ Minimal data collection</li>
                            <li>✓ Secure payment processing via PayPal</li>
                            <li>✓ No call history saved after logout</li>
                        </ul>
                    </div>
                    <div className="privacy-column">
                        <h3>Security</h3>
                        <ul className="privacy-list">
                            <li>✓ All calls encrypted using WebRTC technology</li>
                            <li>✓ Secure HTTPS connection</li>
                            <li>✓ Regular security audits</li>
                            <li>✓ Compliant with international privacy standards</li>
                        </ul>
                    </div>
                </div>
                <Link to="/privacy" className="privacy-link">Read Our Privacy Policy →</Link>
            </section>

            {/* Section 8: Learn More */}
            <section className="content-section">
                <h2 className="section-title">Learn More About International Calling</h2>
                <div className="guides-grid">
                    <Link to="/support" className="guide-card">
                        <span className="guide-icon">📖</span>
                        <span>Complete Guide to International Calling Codes</span>
                    </Link>
                    <Link to="/support" className="guide-card">
                        <span className="guide-icon">📞</span>
                        <span>How to Make International Calls Without a Phone Plan</span>
                    </Link>
                    <Link to="/support" className="guide-card">
                        <span className="guide-icon">💡</span>
                        <span>VoIP vs Traditional Phone: Cost Comparison</span>
                    </Link>
                    <Link to="/support" className="guide-card">
                        <span className="guide-icon">🔐</span>
                        <span>Anonymous Calling: Complete Privacy Guide</span>
                    </Link>
                </div>
                <Link to="/support" className="view-all-link">View All Guides →</Link>
            </section>
        </div>
    );
};

export default HomePage;
